/**
 * Guided OTP delivery setup (D343) — the framework-neutral, tested view-model behind the Admin wizard
 * *Configurações › Integrações › Configurar com assistente*. It turns the server-derived
 * `GET /v1/config/integrations/otp-delivery/status` (+ an optional read-only Meta check) into: the step list with a
 * state per step (resumable — nothing here is local-only truth), the prerequisites checklist, the routing review
 * rows, the test-send outcome classification + polling decision, the per-channel enable readiness, and the form
 * normalizers for the credential steps (reusing the D320 slot normalizers so the wizard and the classic Integrações
 * forms validate identically). Neutral: no country, no provider field invented — every field maps to the real slot
 * schema in `services/vello-api/src/verification/config.ts`.
 */
import { type Result, ok, err } from '@vello/core';
import {
  type ApiError,
  type IntegrationsApi,
  type MetaCheckView,
  type OtpChannel,
  type OtpDeliverySetupStatus,
  type OtpSlotSetupState,
  type SetOtpChannelEnabledInput,
  type SetWhatsappIntegrationInput,
  type SetWhatsappWebhookInput,
  type TestSendRecord,
  type WhatsappIntegrationView,
  type WhatsappWebhookView,
  type IntegrationSlotView,
} from '@vello/api-client';
import { normalizeWhatsappForm, type WhatsappFormIssue } from './integrations.js';

// ---- steps -------------------------------------------------------------------------------------------------

export const OTP_SETUP_STEPS = ['prerequisites', 'whatsapp', 'webhook', 'sms', 'routing', 'test', 'enable'] as const;
export type OtpSetupStepId = (typeof OTP_SETUP_STEPS)[number];

/** `done` — the server shows the step satisfied; `attention` — something is wrong or risky; `todo` — not started. */
export type OtpStepState = 'done' | 'attention' | 'todo';

export type OtpSetupIssue =
  | 'WA_NOT_SAVED'
  | 'WA_KEY_MISSING'
  | 'WA_CONFIG_INCOMPLETE'
  | 'WA_NO_BUSINESS_ACCOUNT_ID'
  | 'WEBHOOK_APP_SECRET_MISSING'
  | 'WEBHOOK_VERIFY_TOKEN_MISSING'
  | 'WEBHOOK_ENV_SOURCED'
  | 'WEBHOOK_NOT_REACHED'
  | 'WEBHOOK_SIGNATURE_REJECTED'
  | 'SMS_NOT_SAVED'
  | 'SMS_KEY_MISSING'
  | 'SMS_CONFIG_INCOMPLETE'
  | 'ROUTING_UNKNOWN_PROVIDER'
  | 'ROUTING_SMS_FALLBACK_OFF'
  | 'WA_UNTESTED'
  | 'WA_TEST_FAILED'
  | 'WA_TEST_STALE'
  | 'WA_TEST_NO_RECEIPT'
  | 'SMS_UNTESTED'
  | 'SMS_TEST_FAILED'
  | 'SMS_TEST_STALE'
  | 'WA_NOT_LIVE'
  | 'SMS_NOT_LIVE'
  | 'ENABLED_UNTESTED'
  | 'PROVIDERS_NOT_VAULT'
  | 'META_CHECK_FAILED';

export interface OtpSetupStep {
  readonly id: OtpSetupStepId;
  readonly state: OtpStepState;
  readonly issues: readonly OtpSetupIssue[];
}

const SUCCESS_TEST: ReadonlySet<TestSendRecord['status']> = new Set(['ACCEPTED', 'SENT', 'DELIVERED', 'READ']);

/** The last test for a slot, judged against the slot's CURRENT version (a test of an older config is stale). */
export type TestVerdict = 'untested' | 'passed' | 'failed' | 'stale';

export function testVerdict(slot: OtpSlotSetupState): TestVerdict {
  const t = slot.lastTest;
  if (t === undefined) return 'untested';
  if (t.slotVersion !== undefined && slot.version !== undefined && t.slotVersion !== slot.version) return 'stale';
  return SUCCESS_TEST.has(t.status) ? 'passed' : 'failed';
}

function slotIssues(slot: OtpSlotSetupState, prefix: 'WA' | 'SMS'): OtpSetupIssue[] {
  if (!slot.exists) return [`${prefix}_NOT_SAVED`];
  const out: OtpSetupIssue[] = [];
  if (!slot.configured) out.push(`${prefix}_KEY_MISSING`);
  if (!slot.configComplete) out.push(`${prefix}_CONFIG_INCOMPLETE`);
  return out;
}

const stateOf = (blocking: readonly OtpSetupIssue[], started: boolean): OtpStepState => (blocking.length === 0 ? 'done' : started ? 'attention' : 'todo');

/** Derive every step's state from the server status (pure). `metaCheck` is the optional result of the read-only Meta check. */
export function deriveOtpSetupSteps(status: OtpDeliverySetupStatus, metaCheck?: MetaCheckView): readonly OtpSetupStep[] {
  const prereq = prerequisiteChecklist(status, metaCheck);
  const prereqFailed = prereq.some((i) => i.state === 'failed');
  // `unknown` (provided, not yet proven) does not hold this step open: the proof is owned by the test step, which
  // does block — otherwise a live setup would resume here forever, since the Meta check result is not persisted.
  // The checklist itself still shows every unproven item as such.
  const prereqOpen = prereq.some((i) => i.state === 'missing');
  const prerequisites: OtpSetupStep = {
    id: 'prerequisites',
    state: prereqFailed ? 'attention' : prereqOpen ? (status.whatsapp.exists || status.sms.exists ? 'attention' : 'todo') : 'done',
    issues: prereqFailed ? ['META_CHECK_FAILED'] : [],
  };

  const waBlocking = slotIssues(status.whatsapp, 'WA');
  const whatsapp: OtpSetupStep = {
    id: 'whatsapp',
    state: stateOf(waBlocking, status.whatsapp.exists),
    issues: [...waBlocking, ...(status.whatsapp.exists && status.whatsapp.businessAccountIdSet !== true ? (['WA_NO_BUSINESS_ACCOUNT_ID'] as const) : [])],
  };

  const w = status.webhook;
  const webhookBlocking: OtpSetupIssue[] = [];
  if (w.appSecret.source === 'none') webhookBlocking.push('WEBHOOK_APP_SECRET_MISSING');
  if (w.verifyToken.source === 'none') webhookBlocking.push('WEBHOOK_VERIFY_TOKEN_MISSING');
  const act = w.activity;
  const signatureRejected = act.lastSignatureRejectedAt !== undefined && (act.lastEventAt === undefined || act.lastSignatureRejectedAt > act.lastEventAt);
  if (signatureRejected) webhookBlocking.push('WEBHOOK_SIGNATURE_REJECTED');
  const webhookInfo: OtpSetupIssue[] = [];
  if (w.appSecret.source === 'env' || w.verifyToken.source === 'env') webhookInfo.push('WEBHOOK_ENV_SOURCED');
  const notReached = w.activityTracked && act.lastHandshakeAt === undefined && act.lastEventAt === undefined;
  if (notReached) webhookInfo.push('WEBHOOK_NOT_REACHED');
  const webhookStarted = w.appSecret.source !== 'none' || w.verifyToken.source !== 'none';
  const webhook: OtpSetupStep = {
    id: 'webhook',
    // Secrets set but Meta never reached us is not "done": receipts (and the automatic SMS on failure) depend on it.
    state: webhookBlocking.length > 0 ? (webhookStarted ? 'attention' : 'todo') : notReached ? 'attention' : 'done',
    issues: [...webhookBlocking, ...webhookInfo],
  };

  const smsBlocking = slotIssues(status.sms, 'SMS');
  const sms: OtpSetupStep = { id: 'sms', state: stateOf(smsBlocking, status.sms.exists), issues: smsBlocking };

  const routingIssues: OtpSetupIssue[] = [];
  if (status.routing.unknownProviderIds.length > 0) routingIssues.push('ROUTING_UNKNOWN_PROVIDER');
  if (!status.policy.smsFallbackEnabled) routingIssues.push('ROUTING_SMS_FALLBACK_OFF');
  const routing: OtpSetupStep = { id: 'routing', state: routingIssues.length === 0 ? 'done' : 'attention', issues: routingIssues };

  const testIssues: OtpSetupIssue[] = [];
  const wv = testVerdict(status.whatsapp);
  const sv = testVerdict(status.sms);
  if (wv === 'untested') testIssues.push('WA_UNTESTED');
  if (wv === 'failed') testIssues.push('WA_TEST_FAILED');
  if (wv === 'stale') testIssues.push('WA_TEST_STALE');
  if (wv === 'passed' && status.whatsapp.lastTest?.status === 'ACCEPTED' && status.whatsapp.lastTest.receiptsExpected) testIssues.push('WA_TEST_NO_RECEIPT');
  if (sv === 'untested') testIssues.push('SMS_UNTESTED');
  if (sv === 'failed') testIssues.push('SMS_TEST_FAILED');
  if (sv === 'stale') testIssues.push('SMS_TEST_STALE');
  const testBlocking = testIssues.filter((i) => i !== 'WA_TEST_NO_RECEIPT');
  const testStarted = wv !== 'untested' || sv !== 'untested';
  const test: OtpSetupStep = { id: 'test', state: testBlocking.length === 0 ? 'done' : testIssues.some((i) => i.endsWith('FAILED')) || testStarted ? 'attention' : 'todo', issues: testIssues };

  const enableIssues: OtpSetupIssue[] = [];
  if (status.delivery.liveProviderMode !== 'vault') enableIssues.push('PROVIDERS_NOT_VAULT');
  if (!status.whatsapp.live) enableIssues.push('WA_NOT_LIVE');
  if (!status.sms.live) enableIssues.push('SMS_NOT_LIVE');
  const enabledButUntested = (status.whatsapp.enabled && wv !== 'passed') || (status.sms.enabled && sv !== 'passed');
  if (enabledButUntested) enableIssues.push('ENABLED_UNTESTED');
  const anyEnabled = status.whatsapp.enabled || status.sms.enabled;
  const enable: OtpSetupStep = {
    id: 'enable',
    state: enableIssues.length === 0 ? 'done' : anyEnabled || enabledButUntested ? 'attention' : 'todo',
    issues: enableIssues,
  };

  return [prerequisites, whatsapp, webhook, sms, routing, test, enable];
}

/** Where a returning operator resumes: the first step the server does not show as done (else the review step). */
export function resumeStep(steps: readonly OtpSetupStep[]): OtpSetupStepId {
  return steps.find((s) => s.state !== 'done')?.id ?? 'enable';
}

export function nextStep(id: OtpSetupStepId): OtpSetupStepId | undefined {
  const i = OTP_SETUP_STEPS.indexOf(id);
  return i >= 0 && i < OTP_SETUP_STEPS.length - 1 ? OTP_SETUP_STEPS[i + 1] : undefined;
}

export function previousStep(id: OtpSetupStepId): OtpSetupStepId | undefined {
  const i = OTP_SETUP_STEPS.indexOf(id);
  return i > 0 ? OTP_SETUP_STEPS[i - 1] : undefined;
}

export function isOtpSetupStepId(value: unknown): value is OtpSetupStepId {
  return typeof value === 'string' && (OTP_SETUP_STEPS as readonly string[]).includes(value);
}

// ---- prerequisites checklist --------------------------------------------------------------------------------

export type PrerequisiteId = 'businessVerification' | 'waba' | 'phoneNumber' | 'accessToken' | 'template' | 'smsGateway';
/** `ok` — evidence proves it; `failed` — evidence disproves it; `unknown` — stored but unproven; `missing` — nothing stored; `manual` — the platform cannot observe it (confirm at the provider). */
export type PrerequisiteState = 'ok' | 'failed' | 'unknown' | 'missing' | 'manual';
export type PrerequisiteEvidence = 'metaCheck' | 'testSend' | 'stored' | 'none';

export interface PrerequisiteItem {
  readonly id: PrerequisiteId;
  readonly state: PrerequisiteState;
  readonly evidence: PrerequisiteEvidence;
  /** A provider reason/status code to show next to a failure (never free text from the provider). */
  readonly detail?: string;
}

const waTestAccepted = (status: OtpDeliverySetupStatus): boolean => status.whatsapp.lastTest !== undefined && SUCCESS_TEST.has(status.whatsapp.lastTest.status);

/** The checklist for step 1, from stored state + test evidence + the optional Meta check. Never optimistic. */
export function prerequisiteChecklist(status: OtpDeliverySetupStatus, metaCheck?: MetaCheckView): readonly PrerequisiteItem[] {
  const wa = status.whatsapp;
  const accepted = waTestAccepted(status);
  const rejectedAuth = wa.lastTest?.status === 'REJECTED' && wa.lastTest.failureReason === 'NOT_AUTHORIZED';
  const phone = metaCheck?.phoneNumber;
  const tpl = metaCheck?.templates;

  // Meta Business verification is not observable with the phone-number token: always confirmed at Meta, never faked.
  const businessVerification: PrerequisiteItem = { id: 'businessVerification', state: 'manual', evidence: 'none' };

  const waba: PrerequisiteItem =
    wa.businessAccountIdSet !== true
      ? { id: 'waba', state: 'missing', evidence: 'none' }
      : tpl !== undefined && tpl.ok
        ? { id: 'waba', state: 'ok', evidence: 'metaCheck' }
        : tpl !== undefined && !tpl.ok && tpl.reason !== 'NO_BUSINESS_ACCOUNT_ID'
          ? { id: 'waba', state: 'failed', evidence: 'metaCheck', detail: tpl.errorCode ?? tpl.reason }
          : { id: 'waba', state: 'unknown', evidence: 'stored' };

  const phoneNumber: PrerequisiteItem =
    wa.phoneNumberIdSet !== true
      ? { id: 'phoneNumber', state: 'missing', evidence: 'none' }
      : phone !== undefined && phone.ok
        ? { id: 'phoneNumber', state: 'ok', evidence: 'metaCheck', ...(phone.codeVerificationStatus !== undefined ? { detail: phone.codeVerificationStatus } : {}) }
        : phone !== undefined && !phone.ok
          ? { id: 'phoneNumber', state: 'failed', evidence: 'metaCheck', detail: phone.errorCode ?? phone.reason }
          : accepted
            ? { id: 'phoneNumber', state: 'ok', evidence: 'testSend' }
            : { id: 'phoneNumber', state: 'unknown', evidence: 'stored' };

  const accessToken: PrerequisiteItem = !wa.configured
    ? { id: 'accessToken', state: 'missing', evidence: 'none' }
    : phone !== undefined && !phone.ok && phone.reason === 'NOT_AUTHORIZED'
      ? { id: 'accessToken', state: 'failed', evidence: 'metaCheck', detail: phone.errorCode ?? phone.reason }
      : (phone !== undefined && phone.ok) || (tpl !== undefined && tpl.ok)
        ? { id: 'accessToken', state: 'ok', evidence: 'metaCheck' }
        : rejectedAuth
          ? { id: 'accessToken', state: 'failed', evidence: 'testSend', detail: wa.lastTest?.failureCode ?? 'NOT_AUTHORIZED' }
          : accepted
            ? { id: 'accessToken', state: 'ok', evidence: 'testSend' }
            : { id: 'accessToken', state: 'unknown', evidence: 'stored' };

  let template: PrerequisiteItem;
  if (wa.locales.length === 0) template = { id: 'template', state: 'missing', evidence: 'none' };
  else if (tpl !== undefined && tpl.ok) {
    const bad = tpl.items.find((i) => !i.found || i.status !== 'APPROVED' || i.category !== 'AUTHENTICATION');
    template = bad === undefined ? { id: 'template', state: 'ok', evidence: 'metaCheck' } : { id: 'template', state: 'failed', evidence: 'metaCheck', detail: bad.found ? `${bad.locale}:${bad.status ?? '?'}/${bad.category ?? '?'}` : `${bad.locale}:NOT_FOUND` };
  } else if (accepted) template = { id: 'template', state: 'ok', evidence: 'testSend' };
  else if (wa.lastTest?.status === 'REJECTED' && wa.lastTest.failureReason === 'NOT_CONFIGURED') template = { id: 'template', state: 'failed', evidence: 'testSend', detail: wa.lastTest.failureCode ?? 'NOT_CONFIGURED' };
  else template = { id: 'template', state: 'unknown', evidence: 'stored' };

  const s = status.sms;
  const smsGateway: PrerequisiteItem = !s.exists
    ? { id: 'smsGateway', state: 'missing', evidence: 'none' }
    : s.lastTest !== undefined && SUCCESS_TEST.has(s.lastTest.status)
      ? { id: 'smsGateway', state: 'ok', evidence: 'testSend' }
      : s.lastTest?.status === 'REJECTED'
        ? { id: 'smsGateway', state: 'failed', evidence: 'testSend', detail: s.lastTest.failureCode ?? s.lastTest.failureReason ?? 'REJECTED' }
        : { id: 'smsGateway', state: s.configured && s.configComplete ? 'unknown' : 'missing', evidence: s.configured ? 'stored' : 'none' };

  return [businessVerification, waba, phoneNumber, accessToken, template, smsGateway];
}

// ---- WhatsApp credentials step (template rows instead of raw JSON) -------------------------------------------

export interface TemplateRow {
  readonly locale: string;
  readonly name: string;
  readonly language: string;
}

/** The wizard's WhatsApp form. `accessToken` is write-only (blank keeps the sealed token). */
export interface WhatsappWizardForm {
  readonly phoneNumberId: string;
  readonly businessAccountId: string;
  readonly apiVersion: string;
  readonly rows: readonly TemplateRow[];
  readonly defaultLocale: string;
  readonly approxCostMinor: string;
  readonly approxCostCurrency: string;
  readonly accessToken: string;
}

export type WhatsappWizardField = 'phoneNumberId' | 'businessAccountId' | 'apiVersion' | 'rows' | 'defaultLocale' | 'approxCost';
export interface WhatsappWizardIssue {
  readonly field: WhatsappWizardField;
  /** Row index for a `rows` issue. */
  readonly row?: number;
  readonly code: WhatsappFormIssue['code'] | 'DUPLICATE_LOCALE' | 'INVALID_TEMPLATE_NAME' | 'INVALID_TEMPLATE_LANGUAGE' | 'INVALID_LOCALE';
}

const LOCALE_TAG = /^[A-Za-z]{2,3}(?:[-_][A-Za-z0-9]{2,8})*$/;
// Mirrors the server's slot validation (`validateWhatsappInput`) so errors surface before the round-trip; the server stays authoritative.
const TEMPLATE_NAME = /^[a-z0-9_]{1,512}$/;
const TEMPLATE_LANGUAGE = /^[A-Za-z]{2,3}(?:_[A-Za-z]{2,4})?$/;

export const emptyTemplateRow = (): TemplateRow => ({ locale: '', name: '', language: '' });

export function whatsappWizardFormFromView(view: WhatsappIntegrationView | undefined): WhatsappWizardForm {
  const c = view?.config ?? {};
  const rows = c.templates !== undefined ? Object.entries(c.templates).map(([locale, t]) => ({ locale, name: t.name, language: t.language })) : [];
  return {
    phoneNumberId: c.phoneNumberId ?? '',
    businessAccountId: c.businessAccountId ?? '',
    apiVersion: c.apiVersion ?? 'v21.0',
    rows: rows.length > 0 ? rows : [emptyTemplateRow()],
    defaultLocale: c.defaultLocale ?? '',
    approxCostMinor: c.approxCostMinor !== undefined ? String(c.approxCostMinor) : '',
    approxCostCurrency: c.approxCostCurrency ?? '',
    accessToken: '',
  };
}

/**
 * Validate + normalize the wizard form into the wire input. `enabled` is carried over from the stored slot — saving
 * credentials NEVER flips a live channel on or off (that is the review step's single, explicit action).
 */
export function normalizeWhatsappWizardForm(form: WhatsappWizardForm, currentlyEnabled: boolean): Result<SetWhatsappIntegrationInput, readonly WhatsappWizardIssue[]> {
  const issues: WhatsappWizardIssue[] = [];
  const rows = form.rows.map((r) => ({ locale: r.locale.trim(), name: r.name.trim(), language: r.language.trim() })).filter((r) => r.locale !== '' || r.name !== '' || r.language !== '');
  const seen = new Set<string>();
  rows.forEach((r, row) => {
    if (r.locale === '' || r.name === '' || r.language === '') issues.push({ field: 'rows', row, code: 'REQUIRED' });
    else {
      if (!LOCALE_TAG.test(r.locale)) issues.push({ field: 'rows', row, code: 'INVALID_LOCALE' });
      if (!TEMPLATE_NAME.test(r.name)) issues.push({ field: 'rows', row, code: 'INVALID_TEMPLATE_NAME' });
      if (!TEMPLATE_LANGUAGE.test(r.language)) issues.push({ field: 'rows', row, code: 'INVALID_TEMPLATE_LANGUAGE' });
    }
    if (r.locale !== '' && seen.has(r.locale)) issues.push({ field: 'rows', row, code: 'DUPLICATE_LOCALE' });
    seen.add(r.locale);
  });
  if (rows.length === 0) issues.push({ field: 'rows', code: 'REQUIRED' });
  const push = (issue: WhatsappWizardIssue): void => {
    if (!issues.some((i) => i.field === issue.field && i.code === issue.code && i.row === issue.row)) issues.push(issue);
  };
  // Checked against the rows directly, so the operator sees it even while a row is still invalid.
  const defaultLocale = form.defaultLocale.trim();
  if (defaultLocale !== '' && rows.length > 0 && !rows.some((r) => r.locale === defaultLocale)) push({ field: 'defaultLocale', code: 'INVALID_DEFAULT_LOCALE' });
  const templates = Object.fromEntries(rows.map((r) => [r.locale, { name: r.name, language: r.language }]));
  const base = normalizeWhatsappForm({
    phoneNumberId: form.phoneNumberId,
    businessAccountId: form.businessAccountId,
    apiVersion: form.apiVersion,
    templatesJson: rows.length > 0 ? JSON.stringify(templates) : '',
    defaultLocale: form.defaultLocale,
    approxCostMinor: form.approxCostMinor,
    approxCostCurrency: form.approxCostCurrency,
    enabled: currentlyEnabled,
    accessToken: form.accessToken,
  });
  if (!base.ok) {
    for (const i of base.error) {
      if (i.field === 'templatesJson') {
        if (issues.every((x) => x.field !== 'rows')) push({ field: 'rows', code: i.code });
      } else push({ field: i.field, code: i.code });
    }
  }
  if (issues.length > 0 || !base.ok) return err(issues);
  return ok(base.value);
}

// ---- webhook step ------------------------------------------------------------------------------------------

export interface WebhookSecretsForm {
  readonly appSecret: string;
  readonly verifyToken: string;
}
export interface WebhookSecretsIssue {
  readonly field: 'appSecret' | 'verifyToken' | 'form';
  readonly code: 'NOTHING_TO_SAVE' | 'INVALID_VALUE';
}

const WEBHOOK_SECRET = /^\S{8,512}$/;

export const emptyWebhookSecretsForm = (): WebhookSecretsForm => ({ appSecret: '', verifyToken: '' });

/** Only the fields the operator typed are sent (a blank field keeps the sealed value). Mirrors the server bounds. */
export function normalizeWebhookSecretsForm(form: WebhookSecretsForm): Result<SetWhatsappWebhookInput, readonly WebhookSecretsIssue[]> {
  const appSecret = form.appSecret.trim();
  const verifyToken = form.verifyToken.trim();
  if (appSecret === '' && verifyToken === '') return err([{ field: 'form', code: 'NOTHING_TO_SAVE' }]);
  const issues: WebhookSecretsIssue[] = [];
  if (appSecret !== '' && !WEBHOOK_SECRET.test(appSecret)) issues.push({ field: 'appSecret', code: 'INVALID_VALUE' });
  if (verifyToken !== '' && !WEBHOOK_SECRET.test(verifyToken)) issues.push({ field: 'verifyToken', code: 'INVALID_VALUE' });
  if (issues.length > 0) return err(issues);
  return ok({ ...(appSecret !== '' ? { appSecret } : {}), ...(verifyToken !== '' ? { verifyToken } : {}) });
}

/** A random verify token (hex) from caller-supplied random bytes (the screen passes `crypto.getRandomValues`). */
export function generateVerifyToken(bytes: Uint8Array): string {
  if (bytes.length < 16) throw new Error('generateVerifyToken needs at least 16 random bytes');
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** The public callback URL Meta must call: this console's API origin + the server-reported path. Meta requires https. */
export function webhookCallbackUrl(apiBaseUrl: string, callbackPath: string): { readonly url: string; readonly https: boolean } {
  const base = apiBaseUrl.trim().replace(/\/+$/, '');
  const path = callbackPath.startsWith('/') ? callbackPath : `/${callbackPath}`;
  return { url: `${base}${path}`, https: /^https:\/\//i.test(base) };
}

// ---- routing review ---------------------------------------------------------------------------------------

export interface RoutingRouteCell {
  readonly channel: OtpChannel;
  readonly providerId: string;
  /** Whether a provider with this id is registered in the verification service. */
  readonly known: boolean;
}

export interface RoutingReviewRow {
  readonly key: string;
  readonly scope: 'default' | 'country';
  readonly countryCode?: string;
  readonly callingCode?: string;
  readonly primary: RoutingRouteCell;
  readonly fallback?: RoutingRouteCell;
  /** The fallback is configured AND the policy allows SMS as a contingency. */
  readonly fallbackEffective: boolean;
}

/** Rows for the read-only routing table: the default first, then countries by calling code. */
export function routingReviewRows(status: OtpDeliverySetupStatus): readonly RoutingReviewRow[] {
  const unknown = new Set(status.routing.unknownProviderIds);
  const cell = (r: { channel: OtpChannel; providerId: string }): RoutingRouteCell => ({ channel: r.channel, providerId: r.providerId, known: !unknown.has(r.providerId) });
  const effective = (fb: { channel: OtpChannel } | undefined): boolean => fb !== undefined && (fb.channel !== 'sms' || status.policy.smsFallbackEnabled);
  const d = status.routing.default;
  const rows: RoutingReviewRow[] = [{ key: 'default', scope: 'default', primary: cell(d.primary), ...(d.fallback !== undefined ? { fallback: cell(d.fallback) } : {}), fallbackEffective: effective(d.fallback) }];
  for (const c of [...status.routing.countries].sort((a, b) => a.callingCode.localeCompare(b.callingCode))) {
    rows.push({ key: `country:${c.countryCode}`, scope: 'country', countryCode: c.countryCode, callingCode: c.callingCode, primary: cell(c.primary), ...(c.fallback !== undefined ? { fallback: cell(c.fallback) } : {}), fallbackEffective: effective(c.fallback) });
  }
  return rows;
}

// ---- test step -------------------------------------------------------------------------------------------

export type TestOutcomeTone = 'success' | 'warning' | 'danger' | 'neutral';

export interface TestOutcomeView {
  readonly tone: TestOutcomeTone;
  /** i18n message id. */
  readonly messageId: string;
  /** No further change is expected (stop polling). */
  readonly terminal: boolean;
}

/**
 * Classify a tracked test-send honestly: only a provider receipt says "delivered"; an SMS gateway acceptance is shown
 * as accepted-without-confirmation, never as delivered.
 */
export function classifyTestSend(record: TestSendRecord): TestOutcomeView {
  switch (record.status) {
    case 'ACCEPTED':
      return record.receiptsExpected ? { tone: 'neutral', messageId: 'wiz.test.outcome.acceptedAwaiting', terminal: false } : { tone: 'warning', messageId: 'wiz.test.outcome.acceptedNoReceipt', terminal: true };
    case 'SENT':
      return { tone: 'neutral', messageId: 'wiz.test.outcome.sent', terminal: false };
    case 'DELIVERED':
      return { tone: 'success', messageId: 'wiz.test.outcome.delivered', terminal: true };
    case 'READ':
      return { tone: 'success', messageId: 'wiz.test.outcome.read', terminal: true };
    case 'FAILED':
      return { tone: 'danger', messageId: 'wiz.test.outcome.failed', terminal: true };
    case 'REJECTED':
      return { tone: 'danger', messageId: 'wiz.test.outcome.rejected', terminal: true };
  }
}

/** Keep polling a test-send only while a receipt can still arrive, bounded by `maxMs` (default: 2 minutes). */
export function shouldPollTestSend(record: TestSendRecord, startedAtMs: number, nowMs: number, maxMs = 120_000): boolean {
  return record.receiptsExpected && !classifyTestSend(record).terminal && nowMs - startedAtMs < maxMs;
}

// ---- review + enable -------------------------------------------------------------------------------------

export type EnableBlocker = 'NOT_SAVED' | 'KEY_MISSING' | 'CONFIG_INCOMPLETE' | 'PROVIDERS_NOT_VAULT';
export type EnableWarning = 'UNTESTED' | 'TEST_FAILED' | 'TEST_STALE' | 'NO_RECEIPT_YET' | 'WEBHOOK_INCOMPLETE' | 'NO_SMS_FALLBACK' | 'SMS_FALLBACK_POLICY_OFF';

export interface ChannelEnableReadiness {
  readonly channel: OtpChannel;
  readonly enabled: boolean;
  readonly live: boolean;
  readonly canEnable: boolean;
  readonly blockers: readonly EnableBlocker[];
  readonly warnings: readonly EnableWarning[];
  /** Enabling with these warnings needs an explicit operator acknowledgement (bad credentials ⇒ real OTP 503). */
  readonly requiresAcknowledgement: boolean;
  /** Pin the enable call to the version the operator reviewed. */
  readonly expectedVersion?: number;
}

export function enableReadiness(status: OtpDeliverySetupStatus, channel: OtpChannel): ChannelEnableReadiness {
  const slot = channel === 'whatsapp' ? status.whatsapp : status.sms;
  const blockers: EnableBlocker[] = [];
  if (!slot.exists) blockers.push('NOT_SAVED');
  else {
    if (!slot.configured) blockers.push('KEY_MISSING');
    if (!slot.configComplete) blockers.push('CONFIG_INCOMPLETE');
  }
  if (status.delivery.liveProviderMode !== 'vault') blockers.push('PROVIDERS_NOT_VAULT');
  const warnings: EnableWarning[] = [];
  const v = testVerdict(slot);
  if (v === 'untested') warnings.push('UNTESTED');
  if (v === 'failed') warnings.push('TEST_FAILED');
  if (v === 'stale') warnings.push('TEST_STALE');
  if (v === 'passed' && slot.lastTest?.status === 'ACCEPTED' && slot.lastTest.receiptsExpected) warnings.push('NO_RECEIPT_YET');
  if (channel === 'whatsapp') {
    if (status.webhook.appSecret.source === 'none' || status.webhook.verifyToken.source === 'none') warnings.push('WEBHOOK_INCOMPLETE');
    if (!status.sms.live) warnings.push('NO_SMS_FALLBACK');
    if (!status.policy.smsFallbackEnabled) warnings.push('SMS_FALLBACK_POLICY_OFF');
  }
  return {
    channel,
    enabled: slot.enabled,
    live: slot.live,
    canEnable: blockers.length === 0 && !slot.enabled,
    blockers,
    warnings,
    requiresAcknowledgement: warnings.includes('UNTESTED') || warnings.includes('TEST_FAILED') || warnings.includes('TEST_STALE'),
    ...(slot.version !== undefined ? { expectedVersion: slot.version } : {}),
  };
}

/** The channels the single "enable" action would switch on, given the operator's selection. */
export function channelsToEnable(status: OtpDeliverySetupStatus, selected: readonly OtpChannel[]): readonly ChannelEnableReadiness[] {
  return (['whatsapp', 'sms'] as const).filter((c) => selected.includes(c)).map((c) => enableReadiness(status, c)).filter((r) => r.canEnable);
}

/** Whether the confirm dialog must carry the acknowledgement (any selected channel with untested/failed/stale evidence). */
export function enableNeedsAcknowledgement(plan: readonly ChannelEnableReadiness[]): boolean {
  return plan.some((r) => r.requiresAcknowledgement);
}

// ---- runtime seams (thin wrappers over the real operator endpoints) ----------------------------------------

interface OtpSetupRuntime {
  readonly integrations: IntegrationsApi;
}

export function getOtpDeliveryStatus(runtime: OtpSetupRuntime): Promise<Result<OtpDeliverySetupStatus, ApiError>> {
  return runtime.integrations.getOtpDeliveryStatus();
}
export function setWhatsappWebhookSecrets(runtime: OtpSetupRuntime, input: SetWhatsappWebhookInput): Promise<Result<WhatsappWebhookView, ApiError>> {
  return runtime.integrations.setWhatsappWebhook(input);
}
export function runWhatsappMetaCheck(runtime: OtpSetupRuntime): Promise<Result<MetaCheckView, ApiError>> {
  return runtime.integrations.checkWhatsappWithMeta();
}
export function setOtpChannelEnabled(runtime: OtpSetupRuntime, channel: OtpChannel, input: SetOtpChannelEnabledInput): Promise<Result<IntegrationSlotView<Record<string, unknown>>, ApiError>> {
  return runtime.integrations.setOtpChannelEnabled(channel, input);
}
export function getTestSend(runtime: OtpSetupRuntime, channel: OtpChannel, providerMessageId: string): Promise<Result<TestSendRecord, ApiError>> {
  return runtime.integrations.getTestSend(channel, providerMessageId);
}
